import React from 'react';
import { Card, useTheme, Box, Button, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { IconExternalLink } from '@tabler/icons-react';
import { useSelector, useDispatch } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { trackClick, trackingActions, trackingTypes } from 'utils/tracking';
import { useNavigate } from 'react-router-dom';
import routePaths from 'routes/routePaths';
import { hasPermission } from 'utils/common';
import * as UserTypes from 'features/user/userTypes';
import LocalStore from 'utils/LocalStore';
import { AppConfig } from 'AppConfig';
import { useFeatureFlag } from 'common/hooks/useFeatureFlag';
import { FEATURE_FLAG } from 'common/featureFlags';

export default function Applications() {
  const currentApp = useSelector((state) => state.user.currentApp);
  const hasEnigmaAccess = useSelector((state) => state.user.hasEnigmaAccess);
  const apps = useSelector((state) => state.user.apps);
  const tenantApps = useSelector((state) => state.user.tenantApps);
  const permissions = useSelector((state) => state.user.permissions);
  const companies = useSelector((state) => state.user.companies);
  const currentTenantCd = useSelector((state) => state.user.currentTenantCd);
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery({ query: '(min-width: 320px) and (max-width: 767px)' });
  const showWorkdayFeature = useFeatureFlag(FEATURE_FLAG.WORKDAY);
  const currentCompany = Array.isArray(companies) ? companies.find((company) => company?.code === currentTenantCd) : null;
  const tenantCampaignEnabled = !!currentCompany?.campaignEnabled;

  const handleLinkClick = async (url, targetApp, dispatchType) => {
    if (targetApp !== currentApp) {
      dispatchType ? dispatch({ type: dispatchType }) : await dispatch(UserTypes.resetAppsTenant());
    }
    navigate(url);
  };

  const openWithPost = (url) => {
      // Get JWT token (replace with your actual source)
      const jwt = LocalStore.getAccessToken();
  
      // Create form
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = url;
      form.target = '_blank';
  
      // Add JWT token as hidden input
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'token';
      input.value = jwt;
      form.appendChild(input);
  
      // Append, submit, and remove form
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
  };

  const applications = [
    {
      title: 'BenInsights',
      description: 'Integrated analytics to understand performance, risk, and opportunity.',
      onAppClick: () => {
        trackClick(trackingTypes.APPLICATION, trackingActions.SWITCH, AppConfig.APP_CODE_FO);
        handleLinkClick(routePaths.frontOffice, AppConfig.APP_CODE_FO);
      },
      hasPermission: apps?.includes(AppConfig.APP_CODE_FO)
    },
    {
      title: 'Plan Optimizer',
      description: 'Compare plan designs and optimize coverage decisions.',
      onAppClick: () => {
        trackClick(trackingTypes.APPLICATION, trackingActions.SWITCH, AppConfig.PERMISSION.PLAN_OPT_VIEW);
        window.open(AppConfig.PLAN_OPTIMIZER_URL, '_blank');
      },
      hasPermission: hasPermission(AppConfig.APP_CODE_PLAN_OPTIMIZE, apps)
    },
    {
      title: 'Premium Optimizer',
      description: 'Evaluate premium scenarios and cost impact.',
      onAppClick: () => {
        trackClick(trackingTypes.APPLICATION, trackingActions.SWITCH, AppConfig.PERMISSION.PREMIUM_OPTIMIZER_VIEW);
        window.open(AppConfig.PREMIUM_OPTIMIZER_URL, '_blank');
      },
      hasPermission: (hasPermission(AppConfig.PERMISSION.PREMIUM_OPTIMIZER_VIEW, permissions) || apps?.includes(AppConfig.APP_CODE_PREMIUM_OPTIMIZER))
    },
    {
      title: 'Enigma',
      description: 'Secure access to member PHI data.',
      onAppClick: () => {
        trackClick(trackingTypes.APPLICATION, trackingActions.SWITCH, AppConfig.ENIGMA_URL);
        openWithPost(AppConfig.ENIGMA_URL);
      },
      hasPermission: hasEnigmaAccess
    },
    {
      title: 'ClientXchange',
      description: 'Centralized tracking for renewal workflows.',
      onAppClick: () => {
        dispatch({
          type: UserTypes.APP_SWITCHED,
          payload: AppConfig.HEADER_MENU_TYPE.STANDARD
        });
        handleLinkClick(routePaths.clientPortal + AppConfig.CLIENT_PORTAL_SUB_URL.ANNUAL_PLAN_RENEWAL, AppConfig.APP_CODE_CLIENT_PORTAL);
      },
      hasPermission: (hasPermission(AppConfig.PERMISSION.CLIENT_PORTAL, permissions) || apps?.includes(AppConfig.APP_CODE_CLIENT_PORTAL))
    },
    {
      title: 'Activate',
      description: 'Move from benefit insight to responsible action with governed outreach.',
      onAppClick: () => {
        // In-BenInsights route: navigate directly, keep FO app/tenant context so the
        // shell stays ready and the BenInsights sidebar + global header stay visible.
        // TODO: Do not keep FO app/context once Activate has its own auth/session
        // handleLinkClick(routePaths.activationEngine + AppConfig.ACTIVATION_ENGINE_SUB_URL.OVERVIEW, AppConfig.APP_CODE_ACTIVATION_ENGINE);
        navigate(routePaths.activationEngine + AppConfig.ACTIVATION_ENGINE_SUB_URL.OVERVIEW);
      },
      // Tenant flag is the master switch; tenant app grant is per-user beneath it.
      hasPermission:
        showWorkdayFeature &&
        tenantCampaignEnabled &&
        tenantApps?.includes(AppConfig.APP_CODE_ACTIVATION_ENGINE)
    }
];
  
  return (
    <Box sx={{ bgcolor: '#FFFFFF', py: '32px', mx: '-60px', px: { xs: 2, sm: 6, md: 6 } }}>
      <Typography
        sx={{
          color: theme.palette.common.black,
          fontFamily: 'Rubik',
          fontSize: '20px',
          fontStyle: 'normal',
          fontWeight: '600',
          lineHeight: '1.875rem',
          letterSpacing: '0.0125rem',                 
        }}
      >
        Applications
      </Typography>
      <Grid container spacing={3} pt={3}>
        {applications.map((application) => {
          return application.hasPermission ? (
            <Grid item size={{ xs: 12, sm: 6, md: 4 }} key={application.title}>
              <Card 
                sx={{
                  borderRadius: '1.5rem',
                  border: '1px solid #959696',
                  boxShadow: 'none',
                  background: theme.palette.common.white,
                  width: '100%',
                  minWidth: 0,
                  maxWidth: '100%',
                  p: { xs: '16px', sm: '1.5rem' }
                }}
              >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: isMobile ? 'flex-start' : 'center',
                  justifyContent: 'space-between',
                  gap: { xs: '4px', sm: '16px' },
                  minWidth: 0,
                  flexWrap: 'nowrap',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    minHeight: 'auto',
                    maxHeight: isMobile ? 'none' : '48px',
                    flexDirection: 'column',
                    justifyContent: isMobile ? 'flex-start' : 'center',
                    flex: '1 1 0',
                    minWidth: 0,
                    overflow: 'hidden',
                    maxWidth: '100%',
                    mt: isMobile ? '16px' : '0px'
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#000',
                      fontFamily: 'Rubik',
                      fontSize: '16px',
                      fontStyle: 'normal',
                      fontWeight: '600',
                      lineHeight: '1.5rem',
                      letterSpacing: '0.01rem',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      width: '100%',
                      minWidth: 0,
                      pr: 1
                    }}
                  >
                    {application.title}
                  </Typography>
                </Box>
              </Box>
                <Box sx={{ 
                  height: '45px',
                  alignSelf: 'stretch',
                  marginTop: '24px',
                  marginBottom: '24px'
                }}>
                  <Typography
                    sx={{
                      color: '#121A22',
                      fontFamily: 'Noto Sans',
                      fontSize: '14px',
                      fontStyle: 'normal',
                      fontWeight: '400',
                      lineHeight: '1.3125rem',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      leadingTrim: 'both',
                      textEdge: 'cap'
                    }}>
                    {application.description || 'No description available.'}
                  </Typography>
                  </Box>
                  <Box
                    sx={{
                      pl: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexShrink: 0
                    }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} />
                    <Button
                      variant="outlined"
                      startIcon={
                        <IconExternalLink size={15} />
                      }
                      sx={{
                        width: 'auto',
                        height: '36px',
                        fontSize: '12px',
                        fontFamily: 'Noto Sans',
                        fontStyle: 'normal',
                        fontWeight: '600',
                        lineHeight: '1.05rem',
                        padding: '6px 16px',
                        borderRadius: '24px',
                        textTransform: 'none',
                        borderColor: '#2F54A5',
                        color: '#2F54A5',
                        leadingTrim: 'both',
                        textAlign: 'center',
                        textEdge: 'cap',
                        '&:hover': {
                          borderColor: '#001A47',
                          color: '#001A47',
                          backgroundColor: '#D8E6FF'
                        }
                      }}  
                      onClick={() => application.onAppClick()}>
                      Launch
                    </Button>
                  </Box>
            </Card>
          </Grid>) : null;
      }  )}
      </Grid>
    </Box>
  );
}